import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import Box from '@mui/material/Box';

import VariantFreeForm from 'catering/components/Forms/MenuItemDetailsForm/VariantFreeForm';
import VariantInputGroup from 'bento-ordering/base/components/VariantInputGroup';
import {
  getVariantName,
  getVariantPrice,
  getCheckboxOrRadio,
  CHECKBOX,
  QUANTITY,
  getDisplayName
} from 'helpers/forms/menuItem';
import StyledVariantBox from 'components/StyledVariantBox';

function VariantGroups({
  formState,
  modifierGroups,
  shouldRenderLastBottomBorder,
  disabled
}) {
  /*
   * Parse the variant data to fit our radioButtonGroup and
   * checkbox group component
   */
  const getParsedVariantOptions = options => {
    const parsedVariantOptions = options.map(
      ({ name, price_variance, slug, disabled }) => ({
        label: disabled ? `${name} (sold out)` : name,
        value: slug,
        price: getVariantPrice(price_variance),
        disabled
      })
    );

    return [
      ...parsedVariantOptions.filter(option => !option.disabled),
      ...parsedVariantOptions.filter(option => option.disabled)
    ];
  };

  /**
   * Track if the number we selected the max selections
   * for a given checkbox. This function counts the number
   * of selected values for the specific checkbox variant
   * in redux form.
   */
  const isMaxVariantOptionsSelected = (maxQuantity, slug) => {
    if (!formState?.[slug] || Object.values(formState).length === 0) {
      return false;
    }

    const selected = Object.values(formState[slug]);
    if (!selected) {
      return false;
    }
    const numberOfSelectedValues = selected.filter(option => option).length;
    return numberOfSelectedValues >= maxQuantity;
  };

  const handleChange = (input, fieldType, event) => {
    const value = fieldType === CHECKBOX ? event : event.target.value;
    input.onChange(value);
  };

  if (!modifierGroups) {
    return null;
  }

  const isSingleModifierGroup = modifierGroups.length === 1;

  /**
   * Render radio inputs or checkbox inputs
   * base on the variant data returned. We are following a similar
   * structure to the catering store. There will only be one variants
   * object. The modifier_groups array under variants represent each variant
   * a user can have for their item. The options array under a field
   * are the modifiers for that particular variant.
   */
  return modifierGroups.map((modifierGroup, index) => {
    const {
      slug,
      max_qty,
      min_qty,
      options,
      helper_text,
      field_type
    } = modifierGroup;

    let borderBottom;

    if (shouldRenderLastBottomBorder && index === modifierGroups.length - 1) {
      // when the Special Instructions Field or the Order Made For Field
      // exists, then we want to make sure that we render the bottom border
      // under the last variant group field
      borderBottom = '1px solid';
    } else if (index === modifierGroups.length - 1) {
      // when the Special Instructions Field and the Order Made For Field
      // don't exist, then we want to make sure that we don't render the
      // last bottom border as the sticky add to cart section will provide
      // that border
      borderBottom = '0';
    } else {
      if (!isSingleModifierGroup) {
        // if there is more than 1 variant modifier group, then we want to make sure
        // that they are separated by the border
        borderBottom = '1px solid';
      } else {
        borderBottom = '0';
      }
    }

    const displayName = getDisplayName(modifierGroup);

    if (field_type === 'free_form') {
      return (
        <StyledVariantBox
          key={slug}
          marginTop={2}
          borderBottom={borderBottom}
          color="borders.primary"
          data-testid={`variant-field-${slug}`}
        >
          <VariantFreeForm
            helperText={helper_text}
            title={displayName}
            fieldName={slug}
          />
        </StyledVariantBox>
      );
    }

    const optionsList = getParsedVariantOptions(options);
    const maxQty = max_qty || optionsList.length;
    const variantName = getVariantName(
      displayName,
      parseInt(min_qty),
      parseInt(maxQty)
    );

    const fieldType =
      field_type === QUANTITY ? QUANTITY : getCheckboxOrRadio(min_qty, maxQty);
    const isFieldDisabled =
      min_qty === 1 && max_qty === 1
        ? false
        : isMaxVariantOptionsSelected(maxQty, slug);
    const isFieldRequired = min_qty > 0;

    return (
      <StyledVariantBox
        key={slug}
        borderBottom={borderBottom}
        color="borders.primary"
        data-testid={`variant-field-${slug}`}
      >
        <Box pt={2.5} pb={2}>
          <Field name={slug}>
            {({ input }) => (
              <VariantInputGroup
                disabled={isFieldDisabled || disabled}
                fieldType={fieldType}
                helperText={helper_text}
                list={optionsList}
                onChange={e => handleChange(input, fieldType, e)}
                required={isFieldRequired}
                title={variantName}
                value={input.value || {}}
                maxQty={max_qty}
                minQty={min_qty}
              />
            )}
          </Field>
        </Box>
      </StyledVariantBox>
    );
  });
}

VariantGroups.propTypes = {
  shouldRenderLastBottomBorder: PropTypes.bool,
  formState: PropTypes.object,
  modifierGroups: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      max_qty: PropTypes.number,
      min_qty: PropTypes.number,
      helper_text: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          slug: PropTypes.string,
          price: PropTypes.number
        })
      )
    })
  ),
  disabled: PropTypes.bool
};

VariantGroups.defaultProps = {
  shouldRenderLastBottomBorder: true
};

export default VariantGroups;
